/**
 * StlPreviewDialog: shows a wireframe Three.js preview of an STL file
 * inside a modal dialog, with the camera auto-rotating around the model.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import * as THREE from 'three';
import { STLLoader } from 'three/addons/loaders/STLLoader.js';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Box } from 'lucide-react';
import type { ProjectFile } from '@/components/files/FileManager';

interface StlPreviewDialogProps {
  file: ProjectFile | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function StlPreviewCanvas({ file }: { file: ProjectFile }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animFrameRef = useRef<number>(0);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const angleRef = useRef(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const cleanup = useCallback(() => {
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = 0;
    }
    if (rendererRef.current) {
      rendererRef.current.dispose();
      rendererRef.current = null;
    }
    sceneRef.current = null;
    cameraRef.current = null;
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    setLoading(true);
    setError(null);

    // Parse STL from the file content (ArrayBuffer)
    let buffer: ArrayBuffer;
    if (file.content instanceof ArrayBuffer) {
      buffer = file.content;
    } else {
      // Shouldn't happen for STL files, but handle gracefully
      setError('Unable to preview: file content is not binary.');
      setLoading(false);
      return;
    }

    let geometry: THREE.BufferGeometry;
    try {
      const loader = new STLLoader();
      geometry = loader.parse(buffer);
      geometry.computeVertexNormals();
    } catch (e) {
      setError('Failed to parse STL file.');
      setLoading(false);
      return;
    }

    if (
      !geometry.attributes.position ||
      geometry.attributes.position.count === 0
    ) {
      setError('STL file appears to be empty.');
      setLoading(false);
      return;
    }

    // Set up renderer
    const width = canvas.clientWidth || 400;
    const height = canvas.clientHeight || 300;
    const renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: true,
    });
    renderer.setSize(width, height, false);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);
    rendererRef.current = renderer;

    // Scene
    const scene = new THREE.Scene();
    sceneRef.current = scene;

    // Fit geometry to a unit box centered at origin
    geometry.computeBoundingBox();
    const box = geometry.boundingBox!;
    const center = new THREE.Vector3();
    box.getCenter(center);
    const size = new THREE.Vector3();
    box.getSize(size);
    const maxDim = Math.max(size.x, size.y, size.z);
    const scale = maxDim > 0 ? 1 / maxDim : 1;

    // Group to center and scale
    const group = new THREE.Group();
    group.scale.setScalar(scale);
    group.position.set(-center.x * scale, -center.y * scale, -center.z * scale);

    // Wireframe mesh using EdgesGeometry for clean edges
    const edgesGeom = new THREE.EdgesGeometry(geometry, 15);
    const lineMat = new THREE.LineBasicMaterial({
      color: 0x7c3aed, // purple-600
      transparent: true,
      opacity: 0.85,
    });
    const lines = new THREE.LineSegments(edgesGeom, lineMat);
    // STL files are in Z-up; rotate to Y-up for the camera
    lines.rotation.x = -Math.PI / 2;
    group.add(lines);
    scene.add(group);

    // Camera
    const camera = new THREE.PerspectiveCamera(45, width / height, 0.01, 100);
    // Position camera so the unit-size model fits in view
    const fovRad = (45 * Math.PI) / 180;
    const dist = 0.8 / Math.tan(fovRad / 2) + 0.5;
    camera.position.set(dist, dist * 0.6, dist);
    camera.lookAt(0, 0, 0);
    cameraRef.current = camera;

    // Ambient light to lightly illuminate any filled faces
    const ambient = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambient);

    setLoading(false);

    // Animation loop: rotate camera around Y axis
    const animate = () => {
      animFrameRef.current = requestAnimationFrame(animate);
      angleRef.current += 0.005;
      const r = Math.sqrt(camera.position.x ** 2 + camera.position.z ** 2);
      camera.position.x = Math.cos(angleRef.current) * r;
      camera.position.z = Math.sin(angleRef.current) * r;
      camera.lookAt(0, 0, 0);
      renderer.render(scene, camera);
    };
    animate();

    // Handle canvas resize
    const resizeObserver = new ResizeObserver(() => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (w > 0 && h > 0) {
        renderer.setSize(w, h, false);
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
      }
    });
    resizeObserver.observe(canvas);

    return () => {
      resizeObserver.disconnect();
      cleanup();
      geometry.dispose();
      edgesGeom.dispose();
      lineMat.dispose();
    };
  }, [file, cleanup]);

  return (
    <div className="relative w-full" style={{ height: '320px' }}>
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground">
          Loading…
        </div>
      )}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center text-xs text-destructive">
          {error}
        </div>
      )}
      <canvas
        ref={canvasRef}
        className="h-full w-full rounded-md"
        style={{ display: error ? 'none' : 'block' }}
      />
    </div>
  );
}

export function StlPreviewDialog({
  file,
  open,
  onOpenChange,
}: StlPreviewDialogProps) {
  if (!file) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-w-xl flex-col gap-4">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Box className="h-4 w-4 text-purple-500" />
            <span className="break-all">{file.filename}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-4 rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
          <span>STL mesh</span>
          <span>{formatFileSize(file.size)}</span>
        </div>

        {/* Three.js wireframe canvas — only mount when dialog is open */}
        {open && <StlPreviewCanvas file={file} />}

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
